import { type ReactNode } from 'react';
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  TopoloAppErrorBoundary,
	TopoloAppRoot,
	createTopoloLazyComponent,
	createTopoloLazyRoute,
	getAppId,
	getAppName,
	resolveTopoloBrowserAuthBaseUrl,
	useAuth,
	type AppRoute,
} from "@topolo-io/app-shell";
import {
  LandingPage,
  LoginPage,
  TopoloAuthCallback,
  TopoloAppLoadingScaffold,
} from '@topolo-io/app-shell';
import { ConfirmDialogHost, TopoloContentSkeleton, ToastViewport, setupBugReporterErrorCapture, setupBrowserErrorAutoSubmit } from '@topolo-io/ui-kit';
import { authClient } from "./lib/topolo-auth";

// Wire global error/console capture so BugReporter (enabled in TopoloAppShell)
// can attach the last few errors to any submitted bug report.
setupBugReporterErrorCapture();

// Auto-submit uncaught browser errors to BugFix so the AI fix → PR loop
// covers the client-side experience, not just worker fetch handlers.
setupBrowserErrorAutoSubmit({
	appName: "topolo-calendar",
	getAuthContext: () => ({}),
});

const APP_ID = getAppId();
const APP_NAME = getAppName();
const HOME_PATH = "/app/calendar";
// The same-origin Auth PROXY, used for this app's own auth data plane.
// Neither VITE_AUTH_API_BASE_URL nor VITE_AUTH_API_URL is injected by the
// deploy pipeline, so in every built environment this is the relative path.
const AUTH_API_URL =
	import.meta.env.VITE_AUTH_API_BASE_URL || import.meta.env.VITE_AUTH_API_URL || "/api/auth";
// The Auth service ORIGIN. LoginPage needs one: it builds the password-reset
// entry with `new URL(authApiUrl)` and starts passkey sign-in with
// `new URL('/api/passkeys/authenticate/options', authApiUrl)`, both of which
// throw on a relative path — the reset link silently disappears and passkey
// sign-in dies. Its own default is this same value.
const AUTH_SERVICE_URL = resolveTopoloBrowserAuthBaseUrl();

const dashboardRoute = createTopoloLazyRoute({ loader: () => import("./admin/routes/DashboardRoute"), fallback: <TopoloContentSkeleton variant="dashboard" />, loading: { shell: "sidebar", content: "dashboard" } });
const CalendarShell = createTopoloLazyComponent<{ children: ReactNode }>({ loader: () => import("./admin/CalendarShell"), fallback: <TopoloAppLoadingScaffold appName={APP_NAME} appId={APP_ID} content="calendar" />, loading: { shell: "sidebar", content: "calendar" } });
const calendarRoute = createTopoloLazyRoute({ loader: () => import("./admin/routes/CalendarRoute"), fallback: <TopoloContentSkeleton variant="calendar" />, loading: { shell: "sidebar" } });
const eventTypesRoute = createTopoloLazyRoute({ loader: () => import("./admin/routes/EventTypesRoute"), fallback: <TopoloContentSkeleton variant="card-grid" />, loading: { shell: "sidebar", content: "card-grid" } });
const availabilityRoute = createTopoloLazyRoute({ loader: () => import("./admin/routes/AvailabilityRoute"), fallback: <TopoloContentSkeleton variant="form" />, loading: { shell: "sidebar", content: "form" } });
const settingsRoute = createTopoloLazyRoute({ loader: () => import("./admin/routes/SettingsRoute"), fallback: <TopoloContentSkeleton variant="form" />, loading: { shell: "sidebar", content: "form" } });
const gettingStartedRoute = createTopoloLazyRoute({ loader: () => import("./admin/routes/GettingStartedRoute"), fallback: <TopoloContentSkeleton variant="actions" />, loading: { shell: "sidebar", content: "actions" } });
const BookingPage = createTopoloLazyComponent<{ handle: string; slug: string | null; embed?: boolean; embedVariant?: "full" | "mini" }>({
	loader: () => import("./booking/BookingPage").then((module) => ({ default: module.BookingPage })),
	fallback: <PublicBookingLoadingFallback />,
});
const publicManageRoute = createTopoloLazyRoute({
	loader: () => import("./booking/PublicManageRoute"),
	fallback: <PublicBookingLoadingFallback />,
});

function PublicBookingLoadingFallback() {
	return (
			<div className="min-h-dvh bg-background bg-[image:var(--bg-gradient)] bg-fixed text-foreground">
			<div className="mx-auto max-w-[1100px] px-[max(1.5rem,env(safe-area-inset-right))] py-10 pb-[calc(2.5rem_+_env(safe-area-inset-bottom))] pl-[max(1.5rem,env(safe-area-inset-left))]">
				<TopoloContentSkeleton variant="detail" />
			</div>
		</div>
	);
}

// Protected product surfaces — each authed host-admin surface is a real
// react-router route rendered into the shared `CalendarShell` chrome's
// `<Outlet/>`. TopoloAppRoot gates every route on a session. Profile/account
// settings are intentionally absent: they are platform-managed in TopoloOne.
// The bare `/app` path redirects to the default (calendar) surface.
const routes: AppRoute[] = [
	{ path: "/app", element: <Navigate to="/app/calendar" replace /> },
	{ path: "/app/dashboard", ...dashboardRoute },
	{ path: "/app/calendar", ...calendarRoute },
	{ path: "/app/event-types", ...eventTypesRoute },
	{ path: "/app/availability", element: <Navigate to="/app/availability/hours" replace /> },
	{ path: "/app/availability/:section", ...availabilityRoute },
	{ path: "/app/settings", element: <Navigate to="/app/settings/profile" replace /> },
	{ path: "/app/settings/:section", ...settingsRoute },
	{ path: "/app/getting-started", ...gettingStartedRoute },
	{ path: "/calendar", element: <Navigate to="/app/calendar" replace /> },
	{ path: "/event-types", element: <Navigate to="/app/event-types" replace /> },
	{ path: "/availability", element: <Navigate to="/app/availability" replace /> },
	{ path: "/settings", element: <Navigate to="/app/settings" replace /> },
	{ path: "/getting-started", element: <Navigate to="/app/getting-started" replace /> },
];

// Public, UNauthenticated customer booking surfaces. TopoloAppRoot evaluates
// public routes before protected app routes, so legacy static app aliases must
// redirect here before the broad public handle matcher can claim them.
const publicRoutes: AppRoute[] = [
	{ path: "/calendar", element: <Navigate to="/app/calendar" replace /> },
	{ path: "/event-types", element: <Navigate to="/app/event-types" replace /> },
	{ path: "/availability", element: <Navigate to="/app/availability" replace /> },
	{ path: "/settings", element: <Navigate to="/app/settings" replace /> },
	{ path: "/getting-started", element: <Navigate to="/app/getting-started" replace /> },
	{ path: "/manage/:token", ...publicManageRoute },
	{ path: "/embed/:handle", element: <PublicEmbedHostRoute /> },
	{ path: "/embed/:handle/:slug", element: <PublicEmbedBookingRoute /> },
	{ path: "/widget/:handle", element: <PublicWidgetHostRoute /> },
	{ path: "/widget/:handle/:slug", element: <PublicWidgetBookingRoute /> },
	{ path: "/:handle", element: <PublicHostRoute /> },
	{ path: "/:handle/:slug", element: <PublicBookingRoute /> },
];

export function isPublicEmbedPath(pathname: string): boolean {
	return (
		pathname === "/embed" ||
		pathname.startsWith("/embed/") ||
		pathname === "/widget" ||
		pathname.startsWith("/widget/")
	);
}

function LazyCalendarShell({ children }: { children: ReactNode }) {
	const CalendarShellComponent = CalendarShell.Component;
	return <CalendarShellComponent>{children}</CalendarShellComponent>;
}

export function App() {
	const isPublicEmbed = isPublicEmbedPath(window.location.pathname);
	return (
		<TopoloAppErrorBoundary appName="Calendar">
			<TopoloAppRoot
				appName={APP_NAME}
				appId={APP_ID}
				auth={authClient}
				routes={routes}
				publicRoutes={publicRoutes}
				home={HOME_PATH}
				insights={isPublicEmbed ? false : undefined}
				publicLanding={<LandingPage appId={APP_ID} authApiUrl={AUTH_SERVICE_URL} />}
				loginElement={<CalendarLogin />}
				callbackElement={<CalendarAuthCallback />}
				shellComponent={LazyCalendarShell}
				loadingFallback={({ loading }) => (
					<TopoloAppLoadingScaffold
						appName={APP_NAME}
						appId={APP_ID}
						shell={loading?.shell ?? "sidebar"}
						content={loading?.content ?? "calendar"}
					/>
				)}
			/>
			<ToastViewport />
			<ConfirmDialogHost />
		</TopoloAppErrorBoundary>
	);
}

/** `/:handle` — public host landing (first/only event type), UNauthenticated. */
function PublicHostRoute() {
	const { handle = "" } = useParams();
	const BookingPageComponent = BookingPage.Component;
	return <BookingPageComponent handle={handle} slug={null} />;
}

/** `/:handle/:slug` — public booking page for a specific event type, UNauthenticated. */
function PublicBookingRoute() {
	const { handle = "", slug = "" } = useParams();
	const BookingPageComponent = BookingPage.Component;
	return <BookingPageComponent handle={handle} slug={slug} />;
}

/** `/embed/:handle` — embeddable booking widget without the full public page chrome. */
function PublicEmbedHostRoute() {
	const { handle = "" } = useParams();
	const BookingPageComponent = BookingPage.Component;
	return <BookingPageComponent handle={handle} slug={null} embed />;
}

/** `/embed/:handle/:slug` — embeddable event booking widget. */
function PublicEmbedBookingRoute() {
	const { handle = "", slug = "" } = useParams();
	const BookingPageComponent = BookingPage.Component;
	return <BookingPageComponent handle={handle} slug={slug} embed />;
}

/** `/widget/:handle` — compact wizard-style booking widget. */
function PublicWidgetHostRoute() {
	const { handle = "" } = useParams();
	const BookingPageComponent = BookingPage.Component;
	return <BookingPageComponent handle={handle} slug={null} embed embedVariant="mini" />;
}

/** `/widget/:handle/:slug` — compact wizard-style event booking widget. */
function PublicWidgetBookingRoute() {
	const { handle = "", slug = "" } = useParams();
	const BookingPageComponent = BookingPage.Component;
	return <BookingPageComponent handle={handle} slug={slug} embed embedVariant="mini" />;
}

/** `/login` — shared ui-kit LoginPage driven by the app-shell auth context. */
function CalendarLogin() {
	const { login, isLoading } = useAuth();
	return (
		<LoginPage
			appId={APP_ID}
			authApiUrl={AUTH_SERVICE_URL}
			autoRedirectToTopolo={false}
			isLoading={isLoading}
			onLogin={async (provider?: string) => {
				await login(provider || "topolo");
			}}
			onPasswordLogin={async (request: Parameters<typeof authClient.loginWithPassword>[0]) => {
				return authClient.loginWithPassword(request);
			}}
		/>
	);
}

/** `/auth/callback` — canonical ui-kit SSO handoff (required by the fleet gate). */
function CalendarAuthCallback() {
	const navigate = useNavigate();
	return (
		<TopoloAuthCallback
			appId={APP_ID}
			appName={APP_NAME}
			fallbackPath={HOME_PATH}
			authClient={authClient}
			navigate={(path, opts) => navigate(path, { replace: opts?.replace })}
		/>
	);
}
